/// <amd-module name="VSS/Diagnostics/UI/Components/Reload/Reload" />
import * as React from "react";

import { wait } from "VSS/Core/Util/Promise";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";

import { Tooltip } from "VSSUI/TooltipEx";

const enum State {
    NotSupported,

    Disabled,

    Enabled
}

const enum ReloadMode {
    Full,
    Hot
}

const enum MessageLevel {
    important = 0,
    info = 1
}

interface IPerfReloadSectionProps {}

interface IPerfReloadSectionState {
    state: State;
    message?: string;
}

const LocalStorageKey = "TFS-LIVE-RELOAD";
const LocalStorageEnabledValue = "enabled";

function readState(): State {
    return tryGetLocalStorageValue(LocalStorageKey) === LocalStorageEnabledValue ? State.Enabled : State.Disabled;
}

function storeState(state: State): void {
    trySetLocalStorageValue(LocalStorageKey, state === State.Enabled ? LocalStorageEnabledValue : "");
}

function logMessage(message: string): void {
    if (console) {
        console.log(`%c Live Reload: ${message}`, "font-weight: bold;");
    }
}

function logFailure(title: string, message: string): void {
    if (console) {
        if (console.group) {
            console.group(`%c Live Reload: ${title}`, "font-weight: bold; color: FireBrick;");
        }

        console.log(`%c ${JSON.parse(message)}`, "color: FireBrick; font-weight: bold;");

        if (console.groupEnd) {
            console.groupEnd();
        }
    }
}

class Reload extends VssComponent<IPerfReloadSectionProps, IPerfReloadSectionState> {
    private static _getInitialState(): State {
        if (!WebSocket) {
            return State.NotSupported;
        }

        return readState();
    }

    private _webSocket: WebSocket | undefined;

    constructor(props: IPerfReloadSectionProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            state: Reload._getInitialState()
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._updateConnectionState();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this._disconnect();
    }

    public render(): JSX.Element {
        const { message } = this.state;
        const { color, tooltip } = this._getStatusInformation();

        return (
            <Tooltip text={tooltip}>
                <span className="perf-section actionable" onClick={this._toggle}>
                    Live Reload
                    <span className="perf-reload-section-status" style={{ backgroundColor: color }} />
                    {message && <span className="perf-reload-section-message">{message}</span>}
                </span>
            </Tooltip>
        );
    }

    private _updateConnectionState(): void {
        const { state } = this.state;
        if (state === State.Enabled) {
            if (!this._webSocket) {
                // Always connect to `codedev.ms`, otherwise we might run into ERR_CERT_COMMON_NAME_INVALID errors for on premise since
                // the certificate used to secure the reload server is the dev fabric one.
                this._webSocket = new WebSocket(`wss://codedev.ms:4455/load`);
                this._webSocket.addEventListener("open", this._onopen);
                this._webSocket.addEventListener("close", this._onclose);
                this._webSocket.addEventListener("error", this._onerror);
                this._webSocket.addEventListener("message", this._onmessage);
            }
        } else if (this._webSocket) {
            this._disconnect();
        }
    }

    private _onopen = () => {
        if (this._webSocket) {
            this._webSocket.send("connect");
        }

        this.forceUpdate();
    };

    private _onclose = () => {
        this._disconnect();
    };

    private _onerror = () => {
        this._disconnect();
    };

    private _onmessage = (event: MessageEvent) => {
        const message: string = event.data;

        let messageType = "message";
        let payload = "";
        const idx = message.indexOf(";");
        if (idx === -1) {
            // Compat mode
            if (message.indexOf("reload") !== -1) {
                messageType = "reload";
            }
        } else {
            messageType = message.substr(0, idx);
            payload = message.substr(idx + 1);
        }

        switch (messageType) {
            case "reload": {
                // Wait a bit for the deployment to finish
                wait(2000).then(() => {
                    if (message.indexOf(";") === -1) {
                        // Compat mode
                    }

                    // Determine if hot reload was requested
                    let mode: ReloadMode = ReloadMode.Full;
                    if (!!payload) {
                        // Escape windows paths for JSON parsing
                        const json = payload.replace(/\\/g, "\\\\");
                        const fileTypes: string[] = JSON.parse(json);

                        // For now we only support hot-reloading scss files, so check if every file is of that type
                        if (fileTypes.every(ft => ft.endsWith("scss"))) {
                            mode = ReloadMode.Hot;
                        }
                    }

                    switch (mode) {
                        case ReloadMode.Full: {
                            window.location.reload();

                            this.setState({
                                message: "✨ Reloading!"
                            });
                            break;
                        }

                        case ReloadMode.Hot: {
                            // For now we only support SCSS, so brute force, just reload all the styles
                            const linkElements = document.getElementsByTagName("link");
                            for (const linkElement in linkElements) {
                                const link = linkElements[linkElement];
                                if (link.rel === "stylesheet") {
                                    try {
                                        // Disable CSP
                                        (link as any).nonce = null;
                                        (link as any).integrity = null;
                                    } catch {
                                        // Ignore
                                    }

                                    link.href += "?";
                                }
                            }

                            logMessage("🔥 Hot-reloading SCSS files");

                            this.setState({
                                message: undefined
                            });
                        }
                    }
                });
                break;
            }

            case "message": {
                const { message, level, payload: messagePayload }: { message: string; level: MessageLevel; payload: string } = JSON.parse(payload);

                // For back compat, if no level is set treat it as important
                if (level === undefined || level === MessageLevel.important) {
                    this.setState({
                        message: message
                    });
                }

                if (messagePayload) {
                    // Assume it's a failure
                    logFailure(message, messagePayload);
                } else {
                    logMessage(message);
                }
            }
        }
    };

    private _disconnect = () => {
        if (this._webSocket) {
            this._webSocket.removeEventListener("open", this._onopen);
            this._webSocket.removeEventListener("close", this._onclose);
            this._webSocket.removeEventListener("error", this._onerror);
            this._webSocket.removeEventListener("message", this._onmessage);

            if (this._webSocket.readyState === this._webSocket.OPEN || this._webSocket.readyState === this._webSocket.CONNECTING) {
                this._webSocket.close();
                this.forceUpdate();
            }

            this._webSocket = undefined;
        }
    };

    private _getStatusInformation(): { color: string; tooltip: string } {
        const { state } = this.state;

        let color = "";
        let tooltip = "";
        if (state === State.NotSupported) {
            color = "#000";
            tooltip = "Not supported by your browser";
        } else if (state === State.Disabled) {
            color = "#ccc";
            tooltip = "Not enabled, click to enable";
        } else if (!this._webSocket) {
            color = "#f00";
            tooltip = "Disconnected";
        } else {
            switch (this._webSocket.readyState) {
                case this._webSocket.CONNECTING:
                    color = "#ffa500";
                    tooltip = "Connecting";
                    break;
                case this._webSocket.OPEN:
                    color = "#228B22";
                    tooltip = "Connected";
                    break;
            }
        }

        return { color, tooltip };
    }

    private _toggle = () => {
        const { state } = this.state;

        // Can't enable if not supported
        if (state !== State.NotSupported) {
            const newState: State = state === State.Disabled ? State.Enabled : State.Disabled;

            // Persist state to re-enable after reload
            storeState(newState);

            this.setState(
                {
                    state: newState
                },
                () => {
                    this._updateConnectionState();
                }
            );
        }
    };
}

Components.add("perfbar.reloadSection", Reload);
